import React from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { formatDateShort, formatTime } from "@/lib/date";
import { TID } from "@/lib/testIds";

/**
 * EditorialRow — full-width split row with image 55% / text 45%.
 * Image alternates left/right by index. Title is the protagonist.
 *
 * Props:
 *   article       article payload
 *   imageRight    bool (defaults to false)
 *   heightClass   optional override for min-height
 */
export default function EditorialRow({ article, imageRight = false, heightClass = "min-h-[220px] md:min-h-[280px] lg:min-h-[320px]" }) {
  if (!article) return null;
  const focal = article.image_focal_point || "50% 40%";
  const dateStr = article.published_at ? formatDateShort(article.published_at) : "";
  const time = article.published_at ? formatTime(article.published_at) : "";

  return (
    <article
      data-testid={TID.newsCard(article.id)}
      className={`grid grid-cols-1 lg:grid-cols-[55%_45%] overflow-hidden bg-white border border-softblue group ${heightClass}`}
    >
      <Link
        to={`/noticia/${article.slug}`}
        className={`relative overflow-hidden bg-blueblack min-h-[200px] ${imageRight ? "lg:order-2" : ""}`}
      >
        {article.main_image && (
          <img
            src={article.main_image}
            alt={article.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover card-hover-image"
            style={{ objectPosition: focal }}
          />
        )}
      </Link>
      <div className={`p-5 sm:p-7 lg:p-8 flex flex-col justify-center ${imageRight ? "lg:order-1" : ""}`}>
        <Link
          to={`/categoria/${article.main_category_slug}`}
          className="text-[12px] uppercase tracking-[0.16em] font-bold text-turquoise hover:text-nightblue"
        >
          {article.main_category_name}
        </Link>
        <Link to={`/noticia/${article.slug}`} className="block mt-3">
          <h2 className="font-serif font-black text-blueblack text-2xl md:text-[28px] lg:text-[32px] leading-[1.1] group-hover:text-petroleum transition-colors line-clamp-3">
            {article.title}
          </h2>
        </Link>
        {article.summary && (
          <p className="mt-3 text-[15px] leading-relaxed text-textgray line-clamp-3">
            {article.summary}
          </p>
        )}
        <div className="mt-4 text-[12px] uppercase tracking-widest text-textgray flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {dateStr}{time ? ` · ${time} hs` : ""}
          </span>
        </div>
      </div>
    </article>
  );
}
